/**
 * Inline stroke icons for the Sign Request Editor, traced 1:1 from
 * `Sign Request Editor.dc.html`. MUI's icon set is filled-glyph based and
 * cannot reproduce the design's 1.85–2.2px hairline strokes, so the editor
 * draws its own. Every icon inherits `currentColor` and takes its box from the
 * `size` prop, exactly like the design's `ic()` helper.
 */
import React from "react";

export interface EditorIconProps {
  /** Square viewport in px. Matches the design's per-usage icon size. */
  size?: number;
  /** Stroke width; the design uses 1.85 for field glyphs and 2–2.5 for chrome. */
  strokeWidth?: number;
  className?: string;
}

const Svg = ({
  size = 18,
  strokeWidth = 1.85,
  children,
  className,
}: EditorIconProps & { children: React.ReactNode }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    focusable="false"
    style={{ display: "block", flexShrink: 0 }}
  >
    {children}
  </svg>
);

/* ---------------------------------------------------------------- chrome -- */

export const CloseIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2} {...props}>
    <path d="M18 6L6 18M6 6l12 12" />
  </Svg>
);

export const ChevronRightIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2} {...props}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
);

export const ChevronLeftIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2.2} {...props}>
    <path d="M15 18l-6-6 6-6" />
  </Svg>
);

export const ChevronDownIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2.2} {...props}>
    <path d="M6 9l6 6 6-6" />
  </Svg>
);

export const CheckIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2.2} {...props}>
    <path d="M20 6L9 17l-5-5" />
  </Svg>
);

export const PlusIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2.2} {...props}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

export const MinusIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2.2} {...props}>
    <path d="M5 12h14" />
  </Svg>
);

export const SearchIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2} {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </Svg>
);

export const PanelToggleIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={1.9} {...props}>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M9 3v18" />
  </Svg>
);

export const FitScreenIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={1.9} {...props}>
    <path d="M3 7V3h4M21 7V3h-4M3 17v4h4M21 17v4h-4" />
  </Svg>
);

export const UndoIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={1.9} {...props}>
    <path d="M3 7v6h6M21 17a9 9 0 1 1-3-6.7L21 13" />
  </Svg>
);

export const RedoIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={1.9} {...props}>
    <path d="M21 7v6h-6M3 17a9 9 0 1 0 3-6.7L3 13" />
  </Svg>
);

export const SparkleIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={1.9} {...props}>
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
  </Svg>
);

export const DragHandleIcon = ({
  size = 14,
  className,
}: Omit<EditorIconProps, "strokeWidth">) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    className={className}
    aria-hidden="true"
    focusable="false"
    style={{ display: "block", flexShrink: 0 }}
  >
    <circle cx="9" cy="6" r="1" />
    <circle cx="9" cy="12" r="1" />
    <circle cx="9" cy="18" r="1" />
    <circle cx="15" cy="6" r="1" />
    <circle cx="15" cy="12" r="1" />
    <circle cx="15" cy="18" r="1" />
  </svg>
);

export const PaperclipIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2} {...props}>
    <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l9.19-9.19a3 3 0 0 1 4.24 4.24l-8.49 8.49a1 1 0 0 1-1.41-1.41l7.78-7.78" />
  </Svg>
);

export const SendArrowIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2.2} {...props}>
    <path d="M5 12h14" />
    <path d="M13 6l6 6-6 6" />
  </Svg>
);

/* ------------------------------------------------------------ rail tabs -- */

export const DocumentsTabIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </Svg>
);

export const PropertiesTabIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" />
  </Svg>
);

export const AssistantTabIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M18.5 15.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" />
  </Svg>
);

export const InsightsTabIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <path d="M3 17l5-5 4 4 8-8" />
    <path d="M15 8h5v5" />
  </Svg>
);

export const FileIcon = (props: EditorIconProps) => (
  <Svg strokeWidth={2} {...props}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5" />
  </Svg>
);

/* ----------------------------------------------------------- field kit -- */

export const SignatureFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <path d="M3 17c3-1 5-9 8-9s2 6 4 6 2-2 5-2" />
  </Svg>
);

export const InitialsFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <path d="M4 7V4h16v3" />
    <path d="M9 20h6M12 4v16" />
  </Svg>
);

export const TextFieldIcon = InitialsFieldIcon;

export const CheckboxFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <path d="M8 12l3 3 5-6" />
  </Svg>
);

export const RadioFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.5" />
  </Svg>
);

export const DateSignedFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M3 9h18M8 2v4M16 2v4M9 15l2 2 4-4" />
  </Svg>
);

export const DateFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <path d="M3 9h18M8 2v4M16 2v4" />
  </Svg>
);

export const AttachmentFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <path d="M20.9 12.4L12.25 21.05a5.36 5.36 0 0 1-7.58-7.58l8.65-8.65a3.57 3.57 0 0 1 5.05 5.05l-8.66 8.65a1.79 1.79 0 0 1-2.53-2.53l7.99-7.98" />
  </Svg>
);

export const NameFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <circle cx="8" cy="12" r="2.4" />
    <path d="M13 10h5M13 14h5" />
  </Svg>
);

export const EmailFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="M2 6l10 7 10-7" />
  </Svg>
);

export const CompanyFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <path d="M3 21V7l8-4v18M13 21V9l8 4v8" />
    <path d="M7 9h.01M7 13h.01M17 13h.01" />
  </Svg>
);

export const TitleFieldIcon = InitialsFieldIcon;

export const DropdownFieldIcon = (props: EditorIconProps) => (
  <Svg {...props}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M7 9h6M14 11l2 2 2-2" />
  </Svg>
);

/**
 * Field-type → glyph, keyed by `strings.*` field type. Mirrors the design's
 * `fIcon` map so the rail, the placed chip and the properties panel all draw
 * the same mark for a given type.
 */
export const fieldTypeIcons: Record<
  string,
  React.ComponentType<EditorIconProps>
> = {
  signature: SignatureFieldIcon,
  initials: InitialsFieldIcon,
  text: TextFieldIcon,
  checkbox: CheckboxFieldIcon,
  radio_button: RadioFieldIcon,
  "date signed": DateSignedFieldIcon,
  date: DateFieldIcon,
  attachment: AttachmentFieldIcon,
  name: NameFieldIcon,
  email: EmailFieldIcon,
  "company name": CompanyFieldIcon,
  title: TitleFieldIcon,
  drop_down: DropdownFieldIcon,
};

/** Falls back to the text glyph for any type the design does not name. */
export const getFieldTypeIcon = (
  type?: string,
): React.ComponentType<EditorIconProps> =>
  fieldTypeIcons[String(type ?? "").toLowerCase()] ?? TextFieldIcon;
